import React, {useEffect, useRef, useState} from "react";
import PropTypes from 'prop-types';

import {dataManager} from "../managers/DataManager";
import {flowManager} from "../managers/FlowManager";
import {userManager} from "../managers/UserManager";
import {StepHeader} from "../components/StepHeader";
import {StepCategory} from "../components/StepCategory";
import {TileCheckbox} from "../components/TileCheckbox";
import {SubCategoryTile} from "../components/SubCategoryTile";
import {dataKeys} from "../enums/dataKeys";

HomeySelfHostedServerCategoryStep.propTypes = {
  index: PropTypes.number
}

export function HomeySelfHostedServerCategoryStep(props) {
  const name = HomeySelfHostedServerCategoryStep.name;
  const data = dataManager.store(state => state.data); // Data is updated on state change.
  const steps = flowManager.store(state => state.steps); // Steps is updated on state change.
  const stepRef = useRef(null);
  const updateAvailableRef = useRef(null);
  const isMountedRef = useRef(true);
  const [checkValue, setCheckValue] = useState("");
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const homey = userManager.getSelectedHomey();
  const options = {};

  // On mount, focus the step and scroll into view.
  useEffect(() => {
    stepRef.current.focus({ preventScroll: true });
    window.scrollTo({
      top: (stepRef.current.offsetTop - 120),
      behavior: "smooth"
    });
  }, [])

  useEffect(() => {
    const entry = data.find(item => item.name === name);
    if (entry) {
      setCheckValue(entry.value);
    }
  }, [data, name])

  useEffect(() => {
    if (!homey) return;
    isMountedRef.current = true;

    // On mount, check if the selected Homey Self-Hosted Server is up-to-date
    function getUpdateAvailable() {
      homey.authenticate()
        .then((homeyApi) => {
          return homeyApi.updates.getUpdates();
        })
        .then((updates) => {
          if (!isMountedRef.current) return
          if (Array.isArray(updates)) {
            setUpdateAvailable(updates.length > 0);
          } else {
            console.error("Updates is not an array: ", updates);
          }
        })
        .catch((error) => {
          console.error("Couldn't get updates:", error.message);
        });
    }

    if (!updateAvailableRef.current) {
      updateAvailableRef.current = true;
      getUpdateAvailable();
    }

    return () => {
      isMountedRef.current = false;
    }
  }, [homey, name, props.index])

  function handleSelect(event) {
    const value = event.target.value;
    const data = options[value].data;
    const action = data.action;
    const entry = { name, value, ...data };

    flowManager.nextActionStep(props.index, entry, action);
  }

  // Add-ons
  options[`addOns-0`] = {
    title: "Homey Bridge",
    icon: "homey-bridge",
    data: { ...HomeySelfHostedServerCategoryStep.homeyBridge }
  };
  options[`addOns-1`] = {
    title: "Homey Portal",
    icon: "homey-portal",
    data: { ...HomeySelfHostedServerCategoryStep.homeyPortal }
  };
  options[`addOns-2`] = {
    title: "Homey Energy Dongle",
    icon: "energy-dongle",
    data: { ...HomeySelfHostedServerCategoryStep.homeyEnergyDongle }
  };

  // Apps & Devices
  options[`appsAndDevices-0`] = {
    title: "Apps",
    icon: "apps",
    data: { ...HomeySelfHostedServerCategoryStep.apps }
  };
  options[`appsAndDevices-1`] = {
    title: "Devices",
    icon: "devices",
    data: { ...HomeySelfHostedServerCategoryStep.devices }
  };

  // Automation
  options[`automation-0`] = {
    title: "Flow",
    icon: "flow",
    data: { ...HomeySelfHostedServerCategoryStep.flow }
  };
  options[`automation-1`] = {
    title: "Advanced Flow",
    icon: "advanced-flow",
    data: { ...HomeySelfHostedServerCategoryStep.advancedFlow }
  };
  options[`automation-2`] = {
    title: "Logic",
    icon: "logic",
    data: { ...HomeySelfHostedServerCategoryStep.logic }
  };

  // Monitoring
  options[`monitoring-0`] = {
    title: "Insights",
    icon: "insights",
    data: { ...HomeySelfHostedServerCategoryStep.insights }
  };
  options[`monitoring-1`] = {
    title: "Energy",
    icon: "energy",
    data: { ...HomeySelfHostedServerCategoryStep.energy }
  };
  options[`monitoring-2`] = {
    title: "Dashboards",
    icon: "dashboards",
    data: { ...HomeySelfHostedServerCategoryStep.dashboards }
  };

  // Platform
  options['platform-0'] = {
    title: "Mobile App",
    icon: "phone",
    data: { ...HomeySelfHostedServerCategoryStep.mobileApp }
  };
  options['platform-1'] = {
    title: "Web App",
    icon: "webapp",
    data: { ...HomeySelfHostedServerCategoryStep.webApp }
  };

  // Voice Assistants
  options[`voiceAssistants-0`] = {
    title: "Amazon Alexa",
    icon: "alexa",
    data: { ...HomeySelfHostedServerCategoryStep.alexa }
  };
  options[`voiceAssistants-1`] = {
    title: "Google Assistant",
    icon: "google",
    data: { ...HomeySelfHostedServerCategoryStep.google }
  };
  options[`voiceAssistants-2`] = {
    title: "Siri",
    icon: "siri",
    data: { ...HomeySelfHostedServerCategoryStep.siri }
  };

  // Wireless Technologies
  options[`wireless-0`] = {
    title: "433 MHz",
    icon: "433mhz",
    data: { ...HomeySelfHostedServerCategoryStep["433mhz"] }
  };
  options[`wireless-2`] = {
    title: "Bluetooth",
    icon: "bluetooth",
    data: { ...HomeySelfHostedServerCategoryStep.bluetooth }
  };
  options[`wireless-3`] = {
    title: "Matter",
    icon: "matter",
    data: { ...HomeySelfHostedServerCategoryStep.matter }
  };
  options[`wireless-4`] = {
    title: "Infrared",
    icon: "infrared",
    data: { ...HomeySelfHostedServerCategoryStep.infrared }
  };
  options[`wireless-5`] = {
    title: "Thread",
    icon: "thread",
    data: { ...HomeySelfHostedServerCategoryStep.thread }
  };
  options[`wireless-6`] = {
    title: "Wi-Fi",
    icon: "wifi",
    data: { ...HomeySelfHostedServerCategoryStep.wifi }
  };
  options[`wireless-7`] = {
    title: "Zigbee",
    icon: "zigbee",
    data: { ...HomeySelfHostedServerCategoryStep.zigbee }
  };
  options[`wireless-8`] = {
    title: "Z-Wave",
    icon: "z-wave",
    data: { ...HomeySelfHostedServerCategoryStep.zwave }
  };

  // More
  options[`more-0`] = {
    title: "Family",
    icon: "family",
    data: { ...HomeySelfHostedServerCategoryStep.family }
  };
  options[`more-4`] = {
    title: "Other",
    icon: "other",
    data: { ...HomeySelfHostedServerCategoryStep.other }
  };

  return (
    <>
      <div ref={stepRef} tabIndex={0} className="support-form-step">
        <StepHeader
          index={props.index}
          question="How can we help you with Homey Self-Hosted Server?"
          description="Choose the category that describes your question or problem best."
        >
          <div className={`support-form__warning ${updateAvailable && "is-visible"}`}>
            <div className="support-form__warning-icon"/>
            <div>
              <p className="support-form__warning-title">Homey Self-Hosted Server’s software is out of date.</p>
              <p className="support-form__warning-text">
                Updating Homey Self-Hosted Server to the latest firmware version may resolve your issue.
              </p>
            </div>
          </div>
        </StepHeader>
        <div className="support-form-step__categories">
          <StepCategory heading="Add-ons">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("addOns") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Apps & Devices">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("appsAndDevices") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Automation">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("automation") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Monitoring">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("monitoring") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Platform">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("platform") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Voice Assistants">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("voiceAssistants") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Wireless Technologies">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("wireless") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="More">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("more") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
        </div>
      </div>
      {steps.length === props.index + 1 && (
        <div className="support-form-step">
          <StepHeader
            index={props.index + 1}
            question="Select one of the above options."
          />
        </div>
      )}
    </>
  );
}

HomeySelfHostedServerCategoryStep.homeyBridge = {
  action: "homeyBridge",
  [dataKeys.labels]: ["add-ons", "homey-bridge"],
  [dataKeys.path]: ["Add-ons", "Homey Bridge"]
}
HomeySelfHostedServerCategoryStep.homeyPortal = {
  action: "homeyPortal",
  [dataKeys.labels]: ["add-ons", "homey-portal"],
  [dataKeys.path]: ["Add-ons", "Homey Portal"]
}
HomeySelfHostedServerCategoryStep.homeyEnergyDongle = {
  action: "homeyEnergyDongle",
  [dataKeys.labels]: ["add-ons", "homey-energy-dongle"],
  [dataKeys.path]: ["Add-ons", "Homey Energy Dongle"]
}
HomeySelfHostedServerCategoryStep.apps = {
  action: "apps",
  [dataKeys.labels]: ["apps"],
  [dataKeys.path]: ["Apps"]
}
HomeySelfHostedServerCategoryStep.devices = {
  action: "devices",
  [dataKeys.labels]: ["devices"],
  [dataKeys.path]: ["Devices"]
}
HomeySelfHostedServerCategoryStep.flow = {
  action: "flow",
  [dataKeys.labels]: ["flow"],
  [dataKeys.path]: ["Flow"]
}
HomeySelfHostedServerCategoryStep.advancedFlow = {
  action: "advancedFlow",
  [dataKeys.labels]: ["advanced-flow"],
  [dataKeys.path]: ["Advanced Flow"]
}
HomeySelfHostedServerCategoryStep.logic = {
  action: "logic",
  [dataKeys.labels]: ["logic"],
  [dataKeys.path]: ["Logic"]
}
HomeySelfHostedServerCategoryStep.insights = {
  action: "insights",
  [dataKeys.labels]: ["insights"],
  [dataKeys.path]: ["Insights"]
}
HomeySelfHostedServerCategoryStep.energy = {
  action: "energy",
  [dataKeys.labels]: ["energy"],
  [dataKeys.path]: ["Energy"]
}
HomeySelfHostedServerCategoryStep.dashboards = {
  action: "dashboards",
  [dataKeys.labels]: ["dashboards"],
  [dataKeys.path]: ["Dashboards"]
}
HomeySelfHostedServerCategoryStep.mobileApp = {
  action: "mobileApp",
  [dataKeys.labels]: ["mobile-app"],
  [dataKeys.path]: ["Mobile App"]
}
HomeySelfHostedServerCategoryStep.webApp = {
  action: "webApp",
  [dataKeys.labels]: ["web-app"],
  [dataKeys.path]: ["Web App"]
}
HomeySelfHostedServerCategoryStep.alexa = {
  action: "alexa",
  [dataKeys.labels]: ["alexa"],
  [dataKeys.path]: ["Amazon Alexa"]
}
HomeySelfHostedServerCategoryStep.google = {
  action: "google",
  [dataKeys.labels]: ["google-assistant"],
  [dataKeys.path]: ["Google Assistant"]
}
HomeySelfHostedServerCategoryStep.siri = {
  action: "siri",
  [dataKeys.labels]: ["siri"],
  [dataKeys.path]: ["Siri"]
}
HomeySelfHostedServerCategoryStep["433mhz"] = {
  action: "433mhz",
  [dataKeys.labels]: ["433mhz"],
  [dataKeys.path]: ["433 MHz"]
}
HomeySelfHostedServerCategoryStep.bluetooth = {
  action: "bluetooth",
  [dataKeys.labels]: ["bluetooth"],
  [dataKeys.path]: ["Bluetooth"]
}
HomeySelfHostedServerCategoryStep.matter = {
  action: "matter",
  [dataKeys.labels]: ["matter"],
  [dataKeys.path]: ["Matter"]
}
HomeySelfHostedServerCategoryStep.infrared = {
  action: "infrared",
  [dataKeys.labels]: ["infrared"],
  [dataKeys.path]: ["Infrared"]
}
HomeySelfHostedServerCategoryStep.thread = {
  action: "thread",
  [dataKeys.labels]: ["thread"],
  [dataKeys.path]: ["Thread"]
}
HomeySelfHostedServerCategoryStep.wifi = {
  action: "wifi",
  [dataKeys.labels]: ["wifi"],
  [dataKeys.path]: ["Wi-Fi"]
}
HomeySelfHostedServerCategoryStep.zigbee = {
  action: "zigbee",
  [dataKeys.labels]: ["zigbee"],
  [dataKeys.path]: ["Zigbee"]
}
HomeySelfHostedServerCategoryStep.zwave = {
  action: "zwave",
  [dataKeys.labels]: ["z-wave"],
  [dataKeys.path]: ["Z-Wave"]
}
HomeySelfHostedServerCategoryStep.family = {
  action: "family",
  [dataKeys.labels]: ["family"],
  [dataKeys.path]: ["Family"]
}
HomeySelfHostedServerCategoryStep.other = {
  action: "other",
  [dataKeys.labels]: ["other"],
  [dataKeys.path]: ["Other"]
}

